import { useEffect, useState } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Eye, Loader2, Plus, RotateCcw, Save, Trash2 } from "lucide-react";

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

const TermsRow = ({ row, sIdx, rIdx, total, readOnly, onChange, onMove, onDelete }) => (
  <div className="flex gap-2 items-start" data-testid={`terms-row-${sIdx}-${rIdx}`}>
    <Select value={row.type} onValueChange={(v) => onChange({ ...row, type: v })} disabled={readOnly}>
      <SelectTrigger className="w-32 shrink-0" data-testid={`terms-row-type-${sIdx}-${rIdx}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="paragraph">Paragraph</SelectItem>
        <SelectItem value="bullet">Bullet</SelectItem>
      </SelectContent>
    </Select>
    <Textarea
      value={row.text}
      onChange={(e) => onChange({ ...row, text: e.target.value })}
      rows={Math.min(8, Math.max(2, Math.ceil((row.text || "").length / 110)))}
      disabled={readOnly}
      className="flex-1"
      data-testid={`terms-row-text-${sIdx}-${rIdx}`}
    />
    {!readOnly && (
      <div className="flex flex-col gap-1 shrink-0">
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={rIdx === 0} onClick={() => onMove(-1)} data-testid={`terms-row-up-${sIdx}-${rIdx}`}>
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={rIdx === total - 1} onClick={() => onMove(1)} data-testid={`terms-row-down-${sIdx}-${rIdx}`}>
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={onDelete} data-testid={`terms-row-delete-${sIdx}-${rIdx}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    )}
  </div>
);

const TermsSection = ({ section, sIdx, total, readOnly, onChange, onMove, onDelete }) => {
  const setRows = (rows) => onChange({ ...section, rows });
  return (
    <Card className="border" data-testid={`terms-section-${sIdx}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <span className="font-semibold w-6">{sIdx + 1}.</span>
          <Input
            value={section.title}
            onChange={(e) => onChange({ ...section, title: e.target.value })}
            disabled={readOnly}
            className="font-semibold"
            data-testid={`terms-section-title-${sIdx}`}
          />
          {!readOnly && (
            <>
              <Button size="icon" variant="ghost" disabled={sIdx === 0} onClick={() => onMove(-1)} data-testid={`terms-section-up-${sIdx}`}>
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" disabled={sIdx === total - 1} onClick={() => onMove(1)} data-testid={`terms-section-down-${sIdx}`}>
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="text-red-600" onClick={onDelete} data-testid={`terms-section-delete-${sIdx}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {section.rows.map((row, rIdx) => (
          <TermsRow
            key={rIdx}
            row={row}
            sIdx={sIdx}
            rIdx={rIdx}
            total={section.rows.length}
            readOnly={readOnly}
            onChange={(r) => setRows(section.rows.map((x, i) => (i === rIdx ? r : x)))}
            onMove={(dir) => setRows(move(section.rows, rIdx, dir))}
            onDelete={() => setRows(section.rows.filter((_, i) => i !== rIdx))}
          />
        ))}
        {!readOnly && (
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => setRows([...section.rows, { type: "paragraph", text: "" }])} data-testid={`terms-add-paragraph-${sIdx}`}>
              <Plus className="h-4 w-4 mr-1" /> Paragraph
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRows([...section.rows, { type: "bullet", text: "" }])} data-testid={`terms-add-bullet-${sIdx}`}>
              <Plus className="h-4 w-4 mr-1" /> Bullet
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PlaceholderHelp = ({ placeholders }) => (
  <div className="rounded-md border bg-gray-50 p-3 text-sm space-y-2" data-testid="terms-placeholder-help">
    <div>
      Use <code className="px-1 bg-white border rounded">**text**</code> for bold. Placeholders are filled from Company Info and the client:
    </div>
    <div className="flex flex-wrap gap-2">
      {Object.entries(placeholders || {}).map(([k, v]) => (
        <Badge key={k} variant="outline" className="font-normal" title={v}>
          <code className="mr-1">{`{${k}}`}</code> <span className="text-gray-500">{v}</span>
        </Badge>
      ))}
    </div>
  </div>
);

export const ProposalTermsEditor = ({ user }) => {
  const isAdmin = ["MANAGEMENT_ADMIN", "ADMIN"].includes((user?.role_id || "").toUpperCase());
  const [sections, setSections] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/proposal-terms");
      setSections(res.data.sections || []);
      setMeta(res.data);
    } catch {
      toast.error("Failed to load proposal terms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const validate = () => {
    if (!sections.length) return "Add at least one section";
    const bad = sections.findIndex((s) => !s.title.trim());
    if (bad >= 0) return `Section ${bad + 1} needs a title`;
    return null;
  };

  const cleaned = () => sections.map((s) => ({ ...s, rows: s.rows.filter((r) => r.text.trim()) }));

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      await api.put("/proposal-terms", { sections: cleaned() });
      toast.success("Proposal terms saved. All proposals now use these terms.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Failed to save proposal terms");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      await api.delete("/proposal-terms");
      toast.success("Proposal terms reset to default");
      setResetOpen(false);
      await load();
    } catch {
      toast.error("Failed to reset proposal terms");
    }
  };

  const preview = async () => {
    const err = validate();
    if (err) return toast.error(err);
    const win = window.open("", "_blank");
    setPreviewing(true);
    try {
      const res = await api.post("/proposal-terms/preview-pdf", { sections: cleaned() }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) win.location.href = url;
      else window.open(url, "_blank");
    } catch {
      if (win) win.close();
      toast.error("Failed to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 p-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading proposal terms…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="proposal-terms-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          {meta.is_default ? "Using default terms." : `Last updated ${meta.updated_at ? new Date(meta.updated_at).toLocaleString() : ""} by ${meta.updated_by_name || "—"}.`}
          {!isAdmin && <span className="ml-2 text-orange-700">View only — only admins can edit.</span>}
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button variant="outline" onClick={preview} disabled={previewing} data-testid="terms-preview-btn">
                {previewing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />} Preview PDF
              </Button>
              <Button variant="outline" onClick={() => setResetOpen(true)} data-testid="terms-reset-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> Reset to default
              </Button>
              <Button onClick={save} disabled={saving} data-testid="terms-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save Terms
              </Button>
            </>
          )}
        </div>
      </div>

      <PlaceholderHelp placeholders={meta.placeholders} />
      <div className="text-xs text-gray-500">
        Rows using a billing % placeholder are hidden automatically on a proposal when that client's % is 0.
      </div>

      {sections.map((section, sIdx) => (
        <TermsSection
          key={sIdx}
          section={section}
          sIdx={sIdx}
          total={sections.length}
          readOnly={!isAdmin}
          onChange={(sec) => setSections(sections.map((x, i) => (i === sIdx ? sec : x)))}
          onMove={(dir) => setSections(move(sections, sIdx, dir))}
          onDelete={() => setSections(sections.filter((_, i) => i !== sIdx))}
        />
      ))}

      {isAdmin && (
        <Button variant="outline" onClick={() => setSections([...sections, { title: "New Section:", rows: [{ type: "paragraph", text: "" }] }])} data-testid="terms-add-section-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Section
        </Button>
      )}

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent data-testid="terms-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset proposal terms?</AlertDialogTitle>
            <AlertDialogDescription>
              Your saved terms will be removed and all proposals will use the built-in default terms again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reset} data-testid="terms-reset-confirm">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProposalTermsEditor;
